import { DatePicker, Pagination, Checkbox, Select, Spin, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { orderColumns } from './constants';
import "./frontdesk.scss";
import { openReportWindow, showPdfInWindow } from '../../utils/tableActionHandlers';
import { getUserDetails, makePostCall } from '../../utils/helper';
import { applyYhSearch, runYhSearch } from '../../utils/yhSearch';
import { maxTagTooltip } from '../../utils/selectHelpers';
import { launchViewerOnDualMonitor, openSeriesViewer } from '../../utils/MonitorManager';
import SeriesSelectionModal from '../../components/SeriesSelectionModal';
import PatientOrdersModal, { PatientOrdersModalRef } from '../../components/PatientOrdersModal';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';

import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import AgGridTable from '../../components/AgGridTable';
import { debounce } from 'lodash';
import { FieldLabels, getPacsStatusLabel } from '../../constants/fieldLabels';
import {sortedModalityOptionsNew} from '../../utils/helper';

const { RangePicker } = DatePicker;

// Matches the radiologist page's status filter (pages/pacs statusOptions).
const statusOptions = [
  { label: getPacsStatusLabel('SCANNED'), value: 'SCANNED' },
  { label: 'DRAFTED', value: 'DRAFTED' },
  { label: 'REVIEWED', value: 'REVIEWED' },
  { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  { label: 'NOT FOR REPORTING', value: 'NOT_FOR_REPORTING' },
];

const FrontDeskList = ({ StyledTabsComponent }) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [filters, setFilters] = useState({});
  const userDetails = getUserDetails();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const today = dayjs();
  const twoWeeksAgo = dayjs().subtract(14, 'days');

  // Default range [twoWeeksAgo, today], same as Dispatch.
  const [dateRange, setDateRange] = useState([twoWeeksAgo, today]);
  const [printLoading, setPrintLoading] = useState(false);
  // Modality multi-select (per-user allowed modalities, same as radiologist).
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const { modalityOptions } = useAllowedModalities();
  const sortedModalityOptions = sortedModalityOptionsNew(modalityOptions);

  // Radiologists + HODs for the "Reported By" multi-select filter (mirrors the PACS list).
  const [userList, setUserList] = useState([]);
  const [reportedByOpen, setReportedByOpen] = useState(false);
  const radHodOptions = useMemo(() =>
    (userList || [])
      .filter((user: any) => ['radiologist', 'hod'].includes(user.user_type))
      .map((user: any) => ({ label: `${user.user_fullname} (${user.username})`, value: user.username })),
    [userList]);

  useEffect(() => {
    makePostCall('/user-list', {})
      .then(res => setUserList(res.data?.data || []))
      .catch(() => setUserList([]));
  }, []);

  // Series-selection modal (shared component). Picking series launches the viewer
  // filtered to them. Gated by study-viewing permission.
  const [seriesSelectionModal, setSeriesSelectionModal] = useState<{ open: boolean; studyUID: string | null; record: any }>(
    { open: false, studyUID: null, record: null }
  );

  const handleSelectSeries = (record: any) => {
    const studyUid = record?.pacs_study?.ps_study_uid ?? record?.ps_study_uid ?? record?.po_study_uid;
    if (!studyUid) {
      message.warning('No study images found for this order');
      return;
    }
    setSeriesSelectionModal({ open: true, studyUID: studyUid, record });
  };

  const closeSeriesModal = () => setSeriesSelectionModal(prev => ({ ...prev, open: false }));

  const handleOpenSeries = async (studyUID: string, selectedSeries: string[], record: any) => {
    openSeriesViewer(studyUID, selectedSeries, record);
  };

  // Prior-orders popup for the row's patient (same modal the PACS / dispatch lists use).
  const patientOrdersModalRef = useRef<PatientOrdersModalRef>(null);

  const openViewer = async (record: any) => {
    const studyUid = record?.pacs_study?.ps_study_uid ?? record?.ps_study_uid ?? record?.po_study_uid;
    if (!studyUid) {
      message.warning('No study images found for this order');
      return;
    }
    const viewerWindow = await launchViewerOnDualMonitor(studyUid, record);
    if (!viewerWindow) message.warning('Could not open viewer window');
  };

  const handleShowPatientOrders = (record: any) => {
    patientOrdersModalRef.current?.open(record);
  };

  const filterResults = (tempFilters, page = 1, pageSize = 20, overrideDateRange = undefined) => {
    setOrders({ ...orders, loading: true, data: [] });
    // A YH No. search is exact — it ignores every other filter, including the date range.
    const { pin, filters: effectiveFilters } = applyYhSearch(tempFilters);
    const payload = {
      ...effectiveFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize,
    };

    const effectiveDateRange = !pin && (overrideDateRange !== undefined ? overrideDateRange : dateRange);
    if (effectiveDateRange) {
      payload['from_date'] = dayjs(effectiveDateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(effectiveDateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = effectiveDateRange;
    }

    makePostCall('/frontdesk-list', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 });
      });
  };

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    // Typing a YH No. (patient ID) overrides every other filter, so clear them on screen
    // right away (same reset the Search button does) rather than only after Search.
    const { pin, filters: effective } = applyYhSearch(temp_filters, {});
    if (pin) {
      setFilters(effective);
      setDateRange(null);
      setModalityAllSelected(true);
      return;
    }
    setFilters(temp_filters);
  };

  // YH No. present → reset every other filter and show only that patient's orders.
  const runSearch = () =>
    runYhSearch({
      filters,
      setFilters,
      setDateRange,
      setModalityAllSelected,
      onBeforeSearch: () => setPagination({ current: 1, pageSize: pagination.pageSize }),
      search: f => filterResults(f, 1, pagination.pageSize),
    });

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setModalityAllSelected(true);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({}, 1, pagination.pageSize, null);
  };

  useEffect(() => {
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    debouncedFilter(filters);
  }, [dateRange]);

  const debouncedFilter = useCallback(
    debounce(tempFilters => {
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const viewReport = async record => {
    const orderId = record?.pacs_ord_id ?? record?.pacs_order?.pacs_ord_id;
    if (!orderId) {
      message.error('No report found for this study');
      return;
    }
    const reportWindow = openReportWindow();
    setPrintLoading(true);
    try {
      const res = await makePostCall(
        '/print-acc-report',
        {
          order_id: orderId,
          user_id: userDetails?.username,
          fromDispatch: true,
        },
        { responseType: 'arraybuffer' }
      );
      showPdfInWindow(reportWindow, res.data);
    } catch (err) {
      console.log('Error', err);
      message.error('Failed to view report');
      if (reportWindow && !reportWindow.closed) reportWindow.close();
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <Spin spinning={printLoading}>
      <div>
        {StyledTabsComponent && StyledTabsComponent()}
        <div style={{ padding: '0.75rem 1.25rem 0' }}>
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '0.75rem',
              border: '1px solid #E2E8F1',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
            }}
          >
            {/* Action buttons sit on their own top row (right-aligned), mirroring
                the PACS list toolbar placement. */}
            <div className="filter-toolbar">
              <div className="filter-toolbar-main" />
              <div className="filter-toolbar-actions">
                <button
                  className="btn-primary btn-compact"
                  onClick={() => runSearch()}
                >
                  Search
                </button>
                <button className="btn-secondary btn-compact" onClick={() => { clearFilters(); }}>
                  Clear Search
                </button>
              </div>
            </div>
            {/* Filters: 70% inputs | 30% modality */}
            <div className="filter-layout">
              <div className="disp-filters-row filter-layout-inputs">
                <div className="disp-field disp-field--name">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.PATIENT_NAME}
                    value={filters['pat_name'] || ''}
                    onChange={e => handleFilterChange('pat_name', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--id">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.PATIENT_ID_SHORT}
                    value={filters['po_pin'] || ''}
                    onChange={e => handleFilterChange('po_pin', e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
                  />
                </div>

                <div className="disp-field disp-field--acc">
                  <FyzksInput
                    size="small"
                    placeholder="Acc. No"
                    value={filters['po_acc_no'] || ''}
                    onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--ordno">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.ORDER_NUMBER}
                    value={filters['his_ord_no'] || ''}
                    onChange={e => handleFilterChange('his_ord_no', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--visit">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.VISIT_ID}
                    value={filters['po_visit_number'] || ''}
                    onChange={e => handleFilterChange('po_visit_number', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--ref">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.REFERRING_DOCTOR}
                    value={filters['po_ref_doc'] || ''}
                    onChange={e => handleFilterChange('po_ref_doc', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--reportedby">
                  <Select
                    size="small"
                    showSearch
                    mode="multiple"
                    autoClearSearchValue={false}
                    allowClear
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder={FieldLabels.REPORTED_BY}
                    value={filters['po_reported_by']}
                    style={{ width: '100%' }}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: '16rem' }}
                    options={radHodOptions}
                    optionRender={(option) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox checked={filters['po_reported_by']?.includes(option.value)} style={{ pointerEvents: 'none' }} />
                        <span>{option.label}</span>
                      </div>
                    )}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={val => handleFilterChange('po_reported_by', val)}
                    open={reportedByOpen}
                    onDropdownVisibleChange={setReportedByOpen}
                    onMouseDown={e => {
                      if ((e.target as HTMLElement).closest('.ant-select-dropdown')) return;
                      if (reportedByOpen) { e.preventDefault(); setReportedByOpen(false); }
                    }}
                  />
                </div>

                {/* <div className="disp-field disp-field--aet">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.AE_TITLE}
                    value={filters['ps_sending_aet'] || ''}
                    onChange={e => handleFilterChange('ps_sending_aet', e.target.value)}
                  />
                </div> */}

                <div className="disp-field disp-field--body">
                  <FyzksInput
                    size="small"
                    placeholder="Body Part / Study Desc"
                    value={filters['po_body_part'] || ''}
                    onChange={e => handleFilterChange('po_body_part', e.target.value)}
                  />
                </div>

                {/* Status filter — mirrors the radiologist page (all statuses). */}
                <div className="disp-field disp-field--status">
                  <Select
                    size="small"
                    mode="multiple"
                    allowClear
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder="Status"
                    style={{ width: '100%' }}
                    options={statusOptions}
                    value={filters['pacs_status']}
                    onChange={val => handleFilterChange('pacs_status', val)}
                  />
                </div>

                <div className="disp-field disp-field--date">
                  <RangePicker
                    size="small"
                    style={{ width: '100%' }}
                    placeholder={['Study Date', '']}
                    value={dateRange}
                    onChange={val => {
                      if (val) {
                        setDateRange([val[0], val[1]]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </div>

              </div>

              <div className="filter-layout-modalities">
                <div className="filter-modalities-header">
                  <Checkbox
                    className="filter-modalities-selectall"
                    checked={modalityAllSelected}
                    indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                    onChange={e => {
                      setModalityAllSelected(e.target.checked);
                      handleFilterChange('modality', []);
                    }}
                  >
                    Select all
                  </Checkbox>
                </div>
                <Checkbox.Group
                  value={modalityAllSelected ? sortedModalityOptions.map(o => o.value) : (filters['modality'] || [])}
                  onChange={vals => {
                    const allChecked = vals.length === sortedModalityOptions.length;
                    setModalityAllSelected(allChecked);
                    handleFilterChange('modality', allChecked ? [] : vals);
                  }}
                >
                  {sortedModalityOptions.map(opt => (
                    <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </div>
          </div>
          <div className="orders-list">
            <AgGridTable
              height="calc(100vh - 18rem)"
              loading={orders.loading}
              rowData={orders.data || []}
              rowKey={(rec) => rec?.pacs_ord_id ?? rec?.pacs_order?.pacs_ord_id ?? rec?.po_acc_no}
              columns={orderColumns({
                printReport: viewReport,
                selectSeries: handleSelectSeries,
                showPatientOrders: handleShowPatientOrders,
                openViewer,
                viewReport,
              })}
            />
            {/* External pagination for server-side paging */}
            <div className="pacs-ag-grid-pagination">
              <Pagination
                size="small"
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={orders?.total || 0}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={(page, pageSize) => {
                  setPagination({ current: page, pageSize });
                  filterResults(filters, page, pageSize);
                }}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          </div>
          {/* Series selection modal (shared with PACS / My Worklist) */}
          <SeriesSelectionModal
            open={seriesSelectionModal.open}
            studyUID={seriesSelectionModal.studyUID}
            record={seriesSelectionModal.record}
            onClose={closeSeriesModal}
            onOpenSeries={handleOpenSeries}
          />
          {/* Prior orders popup (same modal the PACS / dispatch lists use) */}
          <PatientOrdersModal
            ref={patientOrdersModalRef}
            onOpenReport={viewReport}
            onOpenViewer={openViewer}
            onViewReport={viewReport}
          />
        </div>
      </div>
    </Spin>
  );
};

export default FrontDeskList;
